import { Box } from '@mui/material';
import {
  add,
  differenceInDays,
  getDay,
  getDaysInMonth,
  getMonth,
  getYear,
  set,
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  commaFormat,
  getDayOfWeek,
  getEffectiveDaysInMonth,
  getMonthListByMax,
} from '../../common/helper';
import NoDataPage from '../commonComponent/NoDataPage';
import { SwSchoolGroupLegend } from '../commonComponent/SwSchoolGroupLegend';

interface Props {
  stepsGoalRateAverage?: boolean[];
  data?: any[];
  startDate: number;
  totalAverage: number;
  from?: string;
  isPdf?: boolean;
}

export const RwwAverageGraphBySchoolGroup: React.FC<Props> = ({
  stepsGoalRateAverage = [false, false],
  data,
  startDate,
  totalAverage,
  from = 'dashboard',
  isPdf = false,
}) => {
  const [rowCount, setRowCount] = useState<number>(1);
  const [rateData, setRateData] = useState<any>([]);
  const { width, ref } = useResizeDetector();
  const showLegend = false;
  let maxValue = 0;

  useEffect(() => {
    if (data) {
      setRowCount(Math.ceil(data.length / 11));
      const newMonths = getMonthListByMax(getMonth(startDate * 1000) + 1);
      const newData = newMonths.map((month: string, idx: number) => {
        const daysInMonth = getEffectiveDaysInMonth(startDate, month);
        const eLevel = data.filter(
          (item: any) =>
            item.schoolGroupName === '초등학교' && item.month === month
        );
        const mLevel = data.filter(
          (item: any) =>
            item.schoolGroupName === '중학교' && item.month === month
        );
        const hLevel = data.filter(
          (item: any) =>
            item.schoolGroupName === '고등학교' && item.month === month
        );
        const eDivider =
          eLevel !== null && eLevel.length > 0
            ? eLevel[0].type === 'walk'
              ? 1
              : daysInMonth
            : 1;
        const mDivider =
          mLevel !== null && mLevel.length > 0
            ? mLevel[0].type === 'walk'
              ? 1
              : daysInMonth
            : 1;
        const hDivider =
          hLevel !== null && hLevel.length > 0
            ? hLevel[0].type === 'walk'
              ? 1
              : daysInMonth
            : 1;
        const eValue =
          eLevel !== null && eLevel.length > 0
            ? eLevel[0].maleCount + eLevel[0].femaleCount
            : 0;
        const mValue =
          mLevel !== null && mLevel.length > 0
            ? mLevel[0].maleCount + mLevel[0].femaleCount
            : 0;
        const hValue =
          hLevel !== null && hLevel.length > 0
            ? hLevel[0].maleCount + hLevel[0].femaleCount
            : 0;
        const eType =
          eLevel !== null && eLevel.length > 0 ? eLevel[0].type : 'read';
        const mType =
          mLevel !== null && mLevel.length > 0 ? mLevel[0].type : 'read';
        const hType =
          hLevel !== null && hLevel.length > 0 ? hLevel[0].type : 'read';
        const newValue = Math.max(eValue, mValue, hValue);
        maxValue = newValue > maxValue ? newValue : maxValue;
        return {
          month: month,
          초등학교:
            eLevel !== null && eLevel.length > 0
              ? (eLevel[0].maleCount + eLevel[0].femaleCount
                  ? (eLevel[0].maleValue + eLevel[0].femaleValue) /
                    ((eLevel[0].maleCount + eLevel[0].femaleCount) * eDivider)
                  : 0
                ).toFixed(eType !== 'walk' ? 2 : 0)
              : 0,
          중학교:
            mLevel !== null && mLevel.length > 0
              ? (mLevel[0].maleCount + mLevel[0].femaleCount
                  ? (mLevel[0].maleValue + mLevel[0].femaleValue) /
                    ((mLevel[0].maleCount + mLevel[0].femaleCount) * mDivider)
                  : 0
                ).toFixed(mType !== 'walk' ? 2 : 0)
              : 0,
          고등학교:
            hLevel !== null && hLevel.length > 0
              ? (hLevel[0].maleCount + hLevel[0].femaleCount
                  ? (hLevel[0].maleValue + hLevel[0].femaleValue) /
                    ((hLevel[0].maleCount + hLevel[0].femaleCount) * hDivider)
                  : 0
                ).toFixed(hType !== 'walk' ? 2 : 0)
              : 0,
        };
      });
      setRateData(newData);
    }
  }, [data]);

  return (
    <>
      <div className='basic_tab_content tab_content' ref={ref}>
        {data && data.length ? (
          <>
            <div
              className='bg_lightgray'
              style={{ width: width, height: '500px' }}
            >
              {!showLegend && <SwSchoolGroupLegend />}

              <ResponsiveContainer width='100%' height='100%'>
                <ComposedChart
                  width={500}
                  height={400}
                  data={rateData}
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 20,
                  }}
                >
                  <defs>
                    <linearGradient id='color' x1='0' y1='0' x2='1' y2='1'>
                      <stop offset='5%' stopColor='#9B66F9' stopOpacity={0.8} />
                      <stop offset='95%' stopColor='#C8C0F7' stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id='color2' x1='0' y1='0' x2='1' y2='1'>
                      <stop offset='5%' stopColor='#2681FF' stopOpacity={0.8} />
                      <stop offset='95%' stopColor='#92BDFF' stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id='color3' x1='0' y1='0' x2='1' y2='1'>
                      <stop offset='5%' stopColor='#FF4975' stopOpacity={0.8} />
                      <stop offset='95%' stopColor='#FFA88C' stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke='#f5f5f5' />
                  <XAxis
                    dataKey='month'
                    unit='월'
                    tickLine={false}
                    padding={{ left: 30, right: 30 }}
                  />
                  <YAxis
                    yAxisId='left'
                    unit=''
                    tickLine={false}
                    domain={[0, maxValue]}
                  />
                  <Tooltip
                    // eslint-disable-next-line react/no-unstable-nested-components
                    content={({ active, payload, label }: any) => {
                      if (active && payload && payload.length) {
                        return (
                          <Box className='graph_tooltip_box'>
                            <p className='graph_tooltip_title'>
                              {payload[0].payload.month}월
                            </p>
                            <div className='graph_tooltip_content_box'>
                              <p>초등학교</p>
                              <p className='graph_tooltip_content_box_desc'>
                                {payload[0].payload['초등학교']}
                              </p>
                            </div>
                            <div className='graph_tooltip_content_box'>
                              <p>중학교</p>
                              <p className='graph_tooltip_content_box_desc'>
                                {payload[0].payload['중학교']}
                              </p>
                            </div>
                            <div className='graph_tooltip_content_box'>
                              <p>고등학교</p>
                              <p className='graph_tooltip_content_box_desc'>
                                {payload[0].payload['고등학교']}
                              </p>
                            </div>
                          </Box>
                        );
                      }
                      return null;
                    }}
                    cursor={false}
                  />
                  {showLegend && (
                    <Legend verticalAlign='top' height={36} iconType='circle' />
                  )}
                  <Bar
                    yAxisId='left'
                    dataKey='초등학교'
                    barSize={10}
                    fill='url(#color)'
                    radius={[12, 12, 0, 0]}
                    legendType='circle'
                  />
                  <Bar
                    yAxisId='left'
                    dataKey='중학교'
                    barSize={10}
                    fill='url(#color2)'
                    radius={[12, 12, 0, 0]}
                    legendType='circle'
                  />
                  <Bar
                    yAxisId='left'
                    dataKey='고등학교'
                    barSize={10}
                    fill='url(#color3)'
                    radius={[12, 12, 0, 0]}
                    legendType='circle'
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div style={{ paddingTop: '2rem' }}>
              <div className='table_style01'>
                {!isPdf && (
                  <div>
                    <table style={{ whiteSpace: 'nowrap' }}>
                      <thead>
                        <tr>
                          <th>월</th>
                          {rateData.map((d: any, idx: number) => (
                            <th key={idx.toString()}>{d.month}월</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>초등학교</td>
                          {rateData.map((d: any, idx: number) => (
                            <td key={idx.toString()}>{d['초등학교']}</td>
                          ))}
                        </tr>
                        <tr>
                          <td>중학교</td>
                          {rateData.map((d: any, idx: number) => (
                            <td key={idx.toString()}>{d['중학교']}</td>
                          ))}
                        </tr>
                        <tr>
                          <td>고등학교</td>
                          {rateData.map((d: any, idx: number) => (
                            <td key={idx.toString()}>{d['고등학교']}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                {isPdf && (
                  <div>
                    {Array(rowCount)
                      .fill(0)
                      .map((_d, idx) => (
                        <table
                          key={idx.toString()}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          <colgroup>
                            {Array(12)
                              .fill(0)
                              .map((d, subidx) => (
                                <col
                                  key={subidx.toString()}
                                  width={`${100 / 12}%`}
                                />
                              ))}
                          </colgroup>
                          <thead>
                            <tr>
                              <th>월</th>
                              {rateData
                                ?.slice(11 * idx, 11 * (idx + 1))
                                .map((d: any, subidx: number) => (
                                  <th key={subidx.toString()}>{d.month}월</th>
                                ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>초등학교</td>
                              {rateData
                                ?.slice(11 * idx, 11 * (idx + 1))
                                .map((d: any, subidx: number) => (
                                  <td key={subidx.toString()}>
                                    {commaFormat(d['초등학교'])}
                                  </td>
                                ))}
                            </tr>
                            <tr>
                              <td>중학교</td>
                              {rateData
                                ?.slice(11 * idx, 11 * (idx + 1))
                                .map((d: any, subidx: number) => (
                                  <td key={subidx.toString()}>
                                    {commaFormat(d['중학교'])}
                                  </td>
                                ))}
                            </tr>
                            <tr>
                              <td>고등학교</td>
                              {rateData
                                ?.slice(11 * idx, 11 * (idx + 1))
                                .map((d: any, subidx: number) => (
                                  <td key={subidx.toString()}>
                                    {commaFormat(d['고등학교'])}
                                  </td>
                                ))}
                            </tr>
                          </tbody>
                        </table>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <Box sx={{ height: '500px' }}>
            <NoDataPage />
          </Box>
        )}
      </div>
    </>
  );
};
